import { EventEmitter } from 'eventemitter3';
import React from 'react';
import AlertType from './AlertType';
import { IAlertsProps } from './Alerts';
import { IAlertAction } from './Alert';
import { IconComponentType } from '@creditflowresearch/uilib/Icon';

interface IAlertInput {
  message?: React.ReactNode | string,
  title?: React.ReactNode,
  timeOut?: number,
  onClick?: () => void,
  icon?: IconComponentType | boolean,
  closable?: boolean,
  onClose?: () => void,
  action?: IAlertAction,
  secondaryAction?: IAlertAction,
  isLoading?: boolean,
}

class AlertManager extends EventEmitter {
  listNotify: IAlertsProps[];

  constructor() {
    super();
    this.listNotify = [];
  }

  private isDuplicate(alert: Partial<IAlertsProps>): IAlertsProps | undefined {
    return this.listNotify.find(
      (a) => a.type === alert.type && a.message === alert.message,
    );
  }

  create(notify: Partial<IAlertsProps>) {
    const defaultNotify: Partial<IAlertsProps> = {
      id: `${ Date.now() }-${ Math.random() }`,
      type: AlertType.INFO,
      title: null,
      message: null,
      timeOut: undefined,
      icon: undefined,
      closable: false,
      action: undefined,
      secondaryAction: undefined,
      isLoading: false,
    };

    const alert = { ...defaultNotify, ...notify } as IAlertsProps;

    if (alert.timeOut === undefined) {
      alert.timeOut = this.calculateTimeOut(alert.message);
    }

    const existing = this.isDuplicate(alert);
    if (existing) {
      this.emit(AlertType.BOUNCE, existing.id);
      return existing;
    }

    this.listNotify.push(alert);
    this.emitChange();

    return alert;
  }

  private calculateTimeOut(message: React.ReactNode | string): number {
    if (typeof message === 'string') {
      return Math.min(10000, Math.max(3000, message.length * 50));
    }
    return 5000;
  }

  info(alert: IAlertInput | string | React.ReactNode) {
    return this.create(this.createAlert(AlertType.INFO, alert));
  }

  success(alert: IAlertInput | string | React.ReactNode) {
    return this.create(this.createAlert(AlertType.SUCCESS, alert));
  }

  warning(alert: IAlertInput | string | React.ReactNode) {
    return this.create(this.createAlert(AlertType.WARNING, alert));
  }

  error(alert: IAlertInput | string | React.ReactNode) {
    return this.create(this.createAlert(AlertType.ERROR, alert));
  }

  createAlert(type: AlertType, alert: IAlertInput | string | React.ReactNode): Partial<IAlertsProps> {
    if (typeof alert === 'string' || React.isValidElement(alert)) {
      return { type, message: alert };
    }

    const {
      message,
      title,
      timeOut,
      onClick,
      icon,
      closable,
      onClose,
      action,
      secondaryAction,
      isLoading,
    } = alert as IAlertInput;

    return {
      type,
      message,
      title,
      timeOut,
      onClick,
      icon,
      closable,
      onClose,
      action,
      secondaryAction,
      isLoading,
    };
  }

  update(alert: IAlertsProps, updates: Partial<IAlertsProps>) {
    const index = this.listNotify.findIndex((n) => n.id === alert.id);
    if (index === -1) return;
    this.listNotify[index] = { ...this.listNotify[index], ...updates };
    this.emitChange();
  }

  remove(alert: IAlertsProps) {
    this.listNotify = this.listNotify.filter((n) => alert.id !== n.id);
    this.emitChange();
  }

  emitChange() {
    this.emit(AlertType.CHANGE, this.listNotify);
  }

  addChangeListener(callback: (alerts: IAlertsProps[]) => void) {
    this.addListener(AlertType.CHANGE, callback);
  }

  removeChangeListener(callback: (alerts: IAlertsProps[]) => void) {
    this.removeListener(AlertType.CHANGE, callback);
  }

  getAlertsList() {
    return this.listNotify;
  }
}

export default new AlertManager();
